
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import DataTable from 'TailwindcssComponents/Tables';
import axios from 'axios';
import FooterTailwind from 'TailwindcssComponents/Footer';
import DeleteDialog from 'components/DeleteDialogBox/deleteDialog';
import { ToastContainer, toast } from 'react-toastify';
import { UserDataContext } from 'context';
import defaultLogo from 'assets/images/logos/defaultlogo.png';

export default function VenueCities() {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const { userData, selectedOrganization } = useContext(UserDataContext);
    const userRole = userData?.isAdmin ? 'admin' : selectedOrganization?.name || 'user';
    const orgId = selectedOrganization?.organization?.id || null;
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [citiesData, setCitiesData] = useState([]);
    const [openDeleteDialogBox, setOpenDeleteDialogBox] = useState();
    const [selectedCityId, setSelectedCityId] = useState(null);
    const [totalItemsCount, setTotalItemsCount] = useState(0);
    const [totalPagesCount, setTotalPagesCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [cityStatus, setCityStatus] = useState(null);
    const [eventCategories, setEventCategories] = useState(null);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [selectedCategoryName, setSelectedCategoryName] = useState();
    const [selectedDate, setSelectedDate] = useState(null);
    const [filteredCities, setFilteredCities] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchCities = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${apiUrl}event-service/city`, { params: { page: currentPage, limit: rowsPerPage, name: searchTerm } });
            if (response.data?.success) {
                setCitiesData(response.data.data);
                setFilteredCities(response.data.data);
                setTotalItemsCount(response.data.totalItems || 0);
                setTotalPagesCount(response.data.totalPages || 0);
            } else throw new Error(response.data?.message || 'Error in fetching cities');
        } catch (error) {
            console.error(error.response?.data?.message || error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleActionClick = (id, action) => {
        if (action === 'view') {
            navigate(`/venueCities/single-venueCity/${id}`);
        }
        else if (action === 'edit') {
            navigate(`/venueCities/edit-venueCities/${id}`);
        }
        else if (action === 'delete') {
            handleDelete(id);
        }
    };

    const handleDelete = async (id) => {
        setSelectedCityId(id);
        setOpenDeleteDialogBox(true);
    };

    const closeDeleteDialogBox = () => {
        setOpenDeleteDialogBox(false);
    };

    const handleDeleteConfirm = async () => {
        try {
            setIsLoading(true);
            const response = await axios.delete(`${apiUrl}event-service/event-venue/${selectedCityId}`,
                {
                    headers: { 'Authorization': `Bearer ${bearerToken}` }
                });
            if (response?.data.success) {
                toast.warning(response?.data.message || 'Event has been deleted');
            }
            else throw new Error(response?.data.message || 'Unable to delete');
        } catch (error) {
            console.error(error.response?.data.message || error.message);
            toast.error(error.response?.data.message || error.message);
        } finally {
            fetchCities();
            setOpenDeleteDialogBox(false);
            setIsLoading(false);
        }
    };

    const filterVenues = (term) => {
        if (!term) {
            setFilteredCities(citiesData); // Reset to all data if search term is empty
            return;
        }
        const lowerCaseTerm = term.toLowerCase();
        const filtered = citiesData.filter((venue) =>
            venue.name.toLowerCase().includes(lowerCaseTerm) // Filter by name
        );
        setFilteredCities(filtered);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        filterVenues(term);
    };

    useEffect(() => {
        fetchCities();
    }, [currentPage, rowsPerPage, cityStatus, selectedDate, searchTerm, bearerToken]);

    const columns = [
        {
            key: 'name', label: 'City Name',
            render: (row) => (
                <div className="flex items-center gap-3">
                    <img
                        src={row.image?.secure_url || defaultLogo}
                        alt={`${row.name} cover`}
                        className="w-10 h-10 rounded-full object-cover"
                    />
                    <span>{row.name}</span>
                </div>
            ),
        },
        { key: 'description', label: 'Description', render: (row) => (row.description) },
        {
            key: 'status',
            label: 'Status',
            render: (row) => (
                <div
                    className={`border rounded ${row.isActive ? 'bg-[#45B369] bg-opacity-15 text-[#45B369] border-[#45B369]' : 'bg-red-100 text-red-700 border-red-500'} w-fit h-7 flex items-center`}
                >
                    <div className="py-1 px-3">{row.isActive ? 'Active' : 'Inactive'}</div>
                </div>
            ),
        },
    ];

    return (
        <TailwindLayout>
            <TopBar />
            <div className="p-12 bg-gray-100 min-h-screen">
                <DataTable
                    title={'Venue Cities'}
                    rows={filteredCities}
                    columns={columns}
                    onActionClick={handleActionClick}
                    options={{ showHeader: true, showHeaderTwo: false, viewOption: true, editOption: true, addoption: false, statusFilterOption: false, dateFilterOption: false, typeFilterOption: false, searchOption: true }}
                    totalCount={totalItemsCount}
                    pagesCount={totalPagesCount}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(limit) => setRowsPerPage(limit)}
                    onPageChange={(page) => setCurrentPage(page)}
                    cityStatus={cityStatus}
                    onStatusChange={(status) => setCityStatus(status)}
                    categories={eventCategories}
                    onCategoryChange={(id) => setSelectedCategoryId(id)}
                    category={selectedCategoryName}
                    onDateChange={(date) => setSelectedDate(date)}
                    filteredDate={selectedDate}
                    onSearch={handleSearch}
                    searcItem={searchTerm}
                    role={userRole}
                    loaderStatus={isLoading}
                    userRole={userRole}
                    addButtonOnlyOption={true}
                    addButtonTitle={"Add Venue City"}
                    handleDialogBoxes={() => navigate("/add-venue-cities")}
                />
            </div>
            <DeleteDialog
                open={openDeleteDialogBox}
                onClose={closeDeleteDialogBox}
                onDelete={handleDeleteConfirm}
                name={'venue'}
            />
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <FooterTailwind />
        </TailwindLayout>
    )
}